import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, AlertTriangle, ChevronLeft, ChevronRight,
  CalendarDays, PlayCircle, Hourglass
} from "lucide-react";

const DAY_MS = 86400000;

function parseDate(d) {
  if (!d) return null;
  const p = new Date(d);
  return isNaN(p.getTime()) ? null : p;
}

function fmtDate(d) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fmtDateFull(d) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function daysBetween(a, b) {
  return Math.round((b - a) / DAY_MS);
}

function startOfDay(d) {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

const STATUS_COLORS = {
  in_progress: { bar: "bg-sky-400", diamond: "bg-sky-400 border-sky-500", label: "In Progress", icon: PlayCircle, text: "text-sky-700" },
  waiting_client: { bar: "bg-amber-400", diamond: "bg-amber-400 border-amber-500", label: "Waiting", icon: Hourglass, text: "text-amber-700" },
  completed: { bar: "bg-emerald-500", diamond: "bg-emerald-500 border-emerald-600", label: "Completed", icon: CheckCircle2, text: "text-emerald-700" },
  overdue: { bar: "bg-red-400", diamond: "bg-red-500 border-red-600", label: "Overdue", icon: AlertTriangle, text: "text-red-600" }
};

export function GanttChart({ project, onMilestoneClick }) {
  const milestones = project?.milestones || [];
  const [weekOffset, setWeekOffset] = useState(0);

  const { chartData, timeRange, hasDateData } = useMemo(() => {
    const now = startOfDay(new Date());
    const projectCreated = parseDate(project?.created_at) || now;

    const items = milestones.map((ms, idx) => {
      const created = parseDate(ms.created_at) || projectCreated;
      const start = parseDate(ms.start_date) || created;
      const end = parseDate(ms.end_date) || parseDate(ms.due_date);
      const completed = parseDate(ms.completed_at);
      const status = ms.status || (ms.done ? "completed" : "in_progress");
      const isOverdue = end && status !== "completed" && now > end;
      return { ...ms, idx, start, end, completed, status, isOverdue };
    });

    const hasDateData = items.some(m => m.start || m.end);

    const baseStart = startOfDay(new Date(now.getTime() - 7 * DAY_MS));
    const viewStart = new Date(baseStart.getTime() + weekOffset * 7 * DAY_MS);
    const viewEnd = new Date(viewStart.getTime() + 8 * 7 * DAY_MS);

    return { chartData: items, timeRange: { start: viewStart, end: viewEnd, now }, hasDateData };
  }, [milestones, project?.created_at, weekOffset]);

  if (milestones.length === 0) {
    return (
      <div className="text-center py-12 text-neutral-400" data-testid="gantt-empty">
        <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p className="text-sm font-medium">No milestones to visualize</p>
        <p className="text-xs mt-1">Add milestones with start/end dates to see the timeline</p>
      </div>
    );
  }

  if (!hasDateData) {
    return (
      <div className="text-center py-12 text-neutral-400" data-testid="gantt-no-dates">
        <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p className="text-sm font-medium">Set dates to see the Gantt timeline</p>
        <p className="text-xs mt-1">Go to the Milestones tab and assign start/end dates</p>
      </div>
    );
  }

  const totalDays = daysBetween(timeRange.start, timeRange.end);
  const todayOffset = daysBetween(timeRange.start, timeRange.now);
  const todayPct = Math.max(0, Math.min(100, (todayOffset / totalDays) * 100));

  const weeks = [];
  for (let i = 0; i < 8; i++) {
    weeks.push(new Date(timeRange.start.getTime() + i * 7 * DAY_MS));
  }

  // Stats by status
  const total = chartData.length;
  const completed = chartData.filter(m => m.status === "completed").length;
  const inProgress = chartData.filter(m => m.status === "in_progress" && !m.isOverdue).length;
  const waiting = chartData.filter(m => m.status === "waiting_client" && !m.isOverdue).length;
  const overdue = chartData.filter(m => m.isOverdue).length;

  return (
    <div className="space-y-4" data-testid="gantt-chart">
      {/* Stats row */}
      <div className="grid grid-cols-5 gap-2">
        <Card className="border-neutral-200 bg-neutral-50">
          <CardContent className="py-2 px-2.5 text-center">
            <p className="text-lg font-bold text-neutral-800">{total}</p>
            <p className="text-[9px] text-neutral-500 uppercase tracking-wider">Total</p>
          </CardContent>
        </Card>
        <Card className="border-sky-200 bg-sky-50">
          <CardContent className="py-2 px-2.5 text-center">
            <p className="text-lg font-bold text-sky-700">{inProgress}</p>
            <p className="text-[9px] text-sky-600 uppercase tracking-wider">In Progress</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-2 px-2.5 text-center">
            <p className="text-lg font-bold text-amber-700">{waiting}</p>
            <p className="text-[9px] text-amber-600 uppercase tracking-wider">Waiting</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="py-2 px-2.5 text-center">
            <p className="text-lg font-bold text-emerald-700">{completed}</p>
            <p className="text-[9px] text-emerald-600 uppercase tracking-wider">Done</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-2 px-2.5 text-center">
            <p className="text-lg font-bold text-red-600">{overdue}</p>
            <p className="text-[9px] text-red-500 uppercase tracking-wider">Overdue</p>
          </CardContent>
        </Card>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => setWeekOffset(o => o - 4)} data-testid="gantt-prev">
          <ChevronLeft className="w-4 h-4 mr-1" /> Earlier
        </Button>
        <button onClick={() => setWeekOffset(0)}
          className="text-xs text-neutral-500 hover:text-emerald-600 transition-colors px-2 py-1 rounded hover:bg-emerald-50"
          data-testid="gantt-today">
          Today
        </button>
        <Button variant="ghost" size="sm" onClick={() => setWeekOffset(o => o + 4)} data-testid="gantt-next">
          Later <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Chart */}
      <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white">
        {/* Header */}
        <div className="flex border-b border-neutral-200 bg-neutral-50">
          <div className="w-[220px] min-w-[220px] px-3 py-2 border-r border-neutral-200">
            <span className="text-xs font-semibold text-neutral-600 uppercase tracking-wider">Milestone</span>
          </div>
          <div className="flex-1 relative flex">
            {weeks.map((w, i) => (
              <div key={i} className="flex-1 px-1 py-2 text-center border-r border-neutral-100 last:border-r-0">
                <p className="text-[10px] font-semibold text-neutral-600">{fmtDate(w)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Rows */}
        <div className="relative">
          {chartData.map((ms, i) => {
            const effectiveStatus = ms.isOverdue ? "overdue" : ms.status;
            const statusCfg = STATUS_COLORS[effectiveStatus] || STATUS_COLORS.in_progress;
            const StatusIcon = statusCfg.icon;

            let barStart = 0;
            let barWidth = 0;

            if (ms.start || ms.end) {
              const sDate = ms.start || ms.end;
              const eDate = ms.status === "completed" && ms.completed ? ms.completed : (ms.end || ms.start);

              const startDay = daysBetween(timeRange.start, sDate);
              const endDay = daysBetween(timeRange.start, eDate);

              barStart = Math.max(0, (startDay / totalDays) * 100);
              barWidth = Math.max(1.5, ((endDay - Math.max(0, startDay)) / totalDays) * 100);
              barWidth = Math.min(barWidth, 100 - barStart);
            }

            return (
              <div
                key={ms.id || i}
                className={`flex border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50/80 transition-colors cursor-pointer group ${
                  ms.isOverdue ? "bg-red-50/30" : ms.is_critical ? "bg-red-50/10 border-l-2 border-l-red-400" : ""
                }`}
                onClick={() => onMilestoneClick?.(ms)}
                data-testid={`gantt-row-${i}`}
              >
                {/* Label */}
                <div className="w-[220px] min-w-[220px] px-3 py-2.5 border-r border-neutral-200 flex items-start gap-2">
                  <StatusIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${statusCfg.text}`} />
                  <div className="min-w-0 flex-1">
                    <p className={`text-xs font-medium truncate ${
                      ms.status === "completed" ? "text-emerald-700 line-through" :
                      ms.isOverdue ? "text-red-700" :
                      ms.is_critical ? "text-red-600 font-bold" :
                      ms.status === "waiting_client" ? "text-amber-800" : "text-neutral-800"
                    }`}>
                      {ms.is_critical && <span className="text-red-500 mr-1" title="Critical Path">!</span>}
                      {ms.title}
                    </p>
                    <div className="flex items-center gap-1 mt-0.5">
                      {ms.start_date && (
                        <span className="text-[9px] text-neutral-400">{fmtDate(parseDate(ms.start_date))}</span>
                      )}
                      {ms.start_date && (ms.end_date || ms.due_date) && (
                        <span className="text-[9px] text-neutral-300">-</span>
                      )}
                      {(ms.end_date || ms.due_date) && (
                        <span className={`text-[9px] ${ms.isOverdue ? "text-red-500 font-medium" : "text-neutral-400"}`}>
                          {fmtDate(parseDate(ms.end_date || ms.due_date))}
                        </span>
                      )}
                      {ms.dependencies?.length > 0 && (
                        <span className="text-[9px] text-blue-500 ml-1" title={`Depends on ${ms.dependencies.length} milestone(s)`}>
                          [{ms.dependencies.length} dep]
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bar area */}
                <div className="flex-1 relative py-2">
                  {/* Week grid lines */}
                  {weeks.map((_, wi) => (
                    <div key={wi} className="absolute top-0 bottom-0 border-r border-neutral-100" style={{ left: `${(wi + 1) * 12.5}%` }} />
                  ))}

                  {/* Today line */}
                  {todayPct >= 0 && todayPct <= 100 && (
                    <div className="absolute top-0 bottom-0 w-px bg-red-400 z-10" style={{ left: `${todayPct}%` }} />
                  )}

                  {/* Bar */}
                  {(ms.start || ms.end) && barWidth > 0 && (
                    <div className="absolute top-1/2 -translate-y-1/2 h-6 flex items-center" style={{ left: `${barStart}%`, width: `${barWidth}%` }}>
                      <div className={`h-full w-full rounded-md ${statusCfg.bar} opacity-75 group-hover:opacity-100 transition-opacity relative overflow-hidden shadow-sm`}>
                        {ms.status === "completed" && (
                          <div className="absolute inset-0 bg-white/20" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.15) 3px, rgba(255,255,255,0.15) 6px)" }} />
                        )}
                        {ms.status === "waiting_client" && (
                          <div className="absolute inset-0" style={{ backgroundImage: "repeating-linear-gradient(90deg, transparent, transparent 4px, rgba(255,255,255,0.2) 4px, rgba(255,255,255,0.2) 8px)" }} />
                        )}
                      </div>
                    </div>
                  )}

                  {/* End date diamond marker */}
                  {(ms.end || ms.due) && (() => {
                    const endDateObj = ms.end || parseDate(ms.due_date);
                    if (!endDateObj) return null;
                    const pct = (daysBetween(timeRange.start, endDateObj) / totalDays) * 100;
                    if (pct < -2 || pct > 102) return null;
                    return (
                      <div className="absolute top-1/2 -translate-y-1/2 z-20" style={{ left: `${Math.max(0, Math.min(98, pct))}%` }}>
                        <div className={`w-3 h-3 rotate-45 border-2 ${statusCfg.diamond}`} />
                      </div>
                    );
                  })()}

                  {/* No dates placeholder */}
                  {!ms.start && !ms.end && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[10px] text-neutral-300 italic">No dates set</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Today label */}
          {todayPct >= 0 && todayPct <= 100 && (
            <div className="absolute -top-1 z-20 transform -translate-x-1/2 pointer-events-none"
              style={{ left: `calc(220px + (100% - 220px) * ${todayPct / 100})` }}>
              <div className="bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-b-md">TODAY</div>
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 text-[10px] text-neutral-500 flex-wrap">
        <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-sm bg-sky-400 inline-block" /> In Progress</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-sm bg-amber-400 inline-block" style={{ backgroundImage: "repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.3) 2px, rgba(255,255,255,0.3) 4px)" }} /> Waiting for Client</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-sm bg-emerald-500 inline-block" /> Completed</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-sm bg-red-400 inline-block" /> Overdue</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rotate-45 border border-neutral-400 inline-block" /> End Date</span>
        <span className="flex items-center gap-1.5"><span className="w-px h-3 bg-red-400 inline-block" /> Today</span>
      </div>
    </div>
  );
}
